import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";

const PURPLE = "#7C3AED";
const API_BASE_RAW = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const API_ROOT = API_BASE_RAW.endsWith("/api") ? API_BASE_RAW : `${API_BASE_RAW}/api`;

const getAuthHeaders = () => {
  let token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("idToken") ||
    localStorage.getItem("id_token") ||
    "";

  if (!token) {
    for (const key of ["user", "auth", "session", "profile"]) {
      try {
        const stored = JSON.parse(localStorage.getItem(key) || "");
        token =
          stored?.token ||
          stored?.accessToken ||
          stored?.access_token ||
          stored?.jwt ||
          stored?.idToken ||
          stored?.id_token ||
          "";
        if (token) break;
      } catch {
        // This storage value is not an auth payload.
      }
    }
  }

  return token
    ? { Authorization: `Bearer ${token}`, "x-access-token": token }
    : {};
};

const candidateName = (candidate = {}) =>
  [candidate.first_name || candidate.firstName, candidate.last_name || candidate.lastName]
    .filter(Boolean)
    .join(" ") ||
  candidate.name ||
  candidate.full_name ||
  "Candidate";

const candidateJobTitles = (candidate = {}) => {
  const assignments = Array.isArray(candidate.job_assignments)
    ? candidate.job_assignments
    : Array.isArray(candidate.jobs_summary)
      ? candidate.jobs_summary
      : [];
  const titles = assignments
    .map((job) => (typeof job === "string" ? job : job?.title || job?.job_title || job?.name))
    .filter(Boolean);

  return titles.length ? titles : [candidate.job_title || candidate.candidate_title || ""].filter(Boolean);
};

export default function CandidatePitchPackageDialog({ open, onClose, candidates = [], onCreate }) {
  const candidateNames = useMemo(
    () => candidates.map(candidateName).filter(Boolean),
    [candidates]
  );
  const jobTitles = useMemo(
    () => [...new Set(candidates.flatMap(candidateJobTitles))],
    [candidates]
  );
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sendError, setSendError] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;

    const names = candidateNames.join(", ");
    const firstJob = jobTitles[0] || "";
    setRecipientName("");
    setRecipientEmail("");
    setSendError("");
    setSending(false);
    setJobTitle(firstJob);
    setSubject(`Candidate profile${names ? `: ${names}` : ""}`);
    setMessage(
      `Hi,\n\nI’d like to share ${names || "this candidate"}${
        firstJob ? ` for the ${firstJob} opportunity` : ""
      }. Their pitch package is included below.\n\nBest,`
    );
  }, [candidateNames, jobTitles, open]);

  const handleCreate = async () => {
    if (sending) return;

    const packageDetails = {
      candidates,
      recipientName: recipientName.trim(),
      recipientEmail: recipientEmail.trim(),
      jobTitle: jobTitle.trim(),
      subject: subject.trim(),
      message: message.trim(),
    };
    setSendError("");
    setSending(true);

    try {
      const response = await fetch(`${API_ROOT}/gmail/compose`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          action: "send",
          channel: "email",
          to_email: packageDetails.recipientEmail,
          contact_name: packageDetails.recipientName,
          candidate_name: candidateNames.join(", "),
          job_title: packageDetails.jobTitle,
          subject: packageDetails.subject,
          body_text: packageDetails.message,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.error || `Unable to send pitch package email (${response.status}).`);
      }

      onCreate?.({ ...packageDetails, delivery: result });
      onClose?.();
    } catch (error) {
      setSendError(error?.message || "Unable to send the pitch package email.");
    } finally {
      setSending(false);
    }
  };

  const candidateLabel = candidateNames.join(", ") || "Selected candidate";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle data-popup-header sx={{ px: 3, py: 2.25 }}>
        <Stack data-popup-header direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                display: "grid",
                placeItems: "center",
                borderRadius: 2,
                bgcolor: "rgba(124,58,237,0.1)",
                color: PURPLE,
              }}
            >
              <SendOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography data-popup-title sx={{ fontSize: 18, fontWeight: 700 }}>Build Pitch Package</Typography>
              <Typography sx={{ fontSize: 13, color: "#667085", mt: 0.2 }}>
                Prepare a pitch package to send for the selected candidate.
              </Typography>
            </Box>
          </Stack>
          <IconButton data-popup-close aria-label="Close pitch package dialog" onClick={onClose} size="small">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={2}>
          <Box sx={{ p: 1.75, borderRadius: 2, bgcolor: "#F9FAFB", border: "1px solid #EAECF0" }}>
            <Typography sx={{ fontSize: 12, color: "#667085", mb: 0.35 }}>Candidate{candidateNames.length === 1 ? "" : "s"}</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#344054" }}>{candidateLabel}</Typography>
          </Box>
          <TextField
            label="Associated job"
            value={jobTitle}
            onChange={(event) => setJobTitle(event.target.value)}
            placeholder="Select or enter the job this pitch supports"
            fullWidth
            size="small"
            helperText={jobTitles.length > 1 ? `Available jobs: ${jobTitles.join(", ")}` : undefined}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Recipient name"
              value={recipientName}
              onChange={(event) => setRecipientName(event.target.value)}
              placeholder="Hiring manager or client"
              fullWidth
              size="small"
            />
            <TextField
              label="Recipient email"
              type="email"
              value={recipientEmail}
              onChange={(event) => setRecipientEmail(event.target.value)}
              placeholder="name@company.com"
              required
              fullWidth
              size="small"
            />
          </Stack>
          <TextField
            label="Email subject"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Pitch message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            fullWidth
            multiline
            minRows={6}
          />
          {sendError && <Alert severity="error">{sendError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions data-popup-footer sx={{ px: 3, py: 2.25, borderTop: "1px solid #EAECF0" }}>
        <Button onClick={onClose} variant="outlined" sx={{ color: "#475467", textTransform: "none", borderRadius: "999px", px: 2.25 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!recipientEmail.trim() || !subject.trim() || !candidates.length || sending}
          sx={{ bgcolor: PURPLE, textTransform: "none", borderRadius: "999px", px: 2.5, "&:hover": { bgcolor: "#6D28D9" } }}
        >
          {sending ? "Sending..." : "Create Pitch Package"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
